/**
 * ChatKit Context - Allows components to trigger ChatKit with context
 * 
 * Usage:
 * - Help Center articles can pass article context
 * - ChatKit will open with pre-populated message or context
 */

import React, { createContext, ReactNode, useCallback, useContext, useState } from 'react';

interface ChatKitContextValue {
  /** Open ChatKit with optional initial message/context */
  openChatKit: (options?: { initialMessage?: string; context?: string }) => void;
  /** Check if ChatKit is currently open */
  isOpen: boolean;
}

const ChatKitContext = createContext<ChatKitContextValue | null>(null);

export function ChatKitProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [pendingContext, setPendingContext] = useState<{ initialMessage?: string; context?: string } | null>(null);

  const openChatKit = useCallback((options?: { initialMessage?: string; context?: string }) => {
    if (options) {
      setPendingContext(options);
    }
    setIsOpen(true);
    // Dispatch event for UserChatKitButton to listen
    window.dispatchEvent(new CustomEvent('chatkit:open', { detail: options }));
  }, []);

  const value: ChatKitContextValue = {
    openChatKit,
    isOpen,
  };

  return (
    <ChatKitContext.Provider value={value}>
      {children}
    </ChatKitContext.Provider>
  );
}

export function useChatKitContext(): ChatKitContextValue {
  const context = useContext(ChatKitContext);
  if (!context) {
    throw new Error('useChatKitContext must be used within ChatKitProvider');
  }
  return context;
}

/**
 * Hook to get pending context (used by UserChatKitButton)
 */
export function usePendingChatKitContext() {
  const [context, setContext] = useState<{ initialMessage?: string; context?: string } | null>(null);

  React.useEffect(() => {
    const handleOpen = (e: CustomEvent) => {
      setContext(e.detail || null);
    };

    window.addEventListener('chatkit:open', handleOpen as EventListener);
    return () => {
      window.removeEventListener('chatkit:open', handleOpen as EventListener);
    };
  }, []);

  const clearContext = useCallback(() => {
    setContext(null);
  }, []);

  return { context, clearContext };
}
